/**
 * 未完事項三張清單（P1-12；`ui-spec` §2 頁 17；`rules §七 G4`／`H4`）
 *
 * 儀表板（每類 5 條）同頁 17（完整）**共用同一個元件** —— 唔准各寫一份。
 * ⚠️ 「缺交收證據」嘅有冇缺，判斷一律喺後端經 `deliveryEvidence()`（唯一實作）→
 *    前端**唔准**自己再判斷（呢度只係照顯示後端畀嘅清單）。
 * ⚠️ 行數 ≤200（硬規則 B）。
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { btnGhost, card, hint as hintStyle } from '@coeldery/ui-kit';
import type { OnSiteStock, PendingOrder } from '../lib/api';

export interface PendingListsProps {
  unsettledEvents: PendingOrder[];
  missingChop: PendingOrder[];
  onSite: OnSiteStock[];
  /** 顯示「重新載入」掣（頁 17 用；儀表板唔需要） */
  onReload?: () => void;
  reloading?: boolean;
}

const mono: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 };
const fmt6 = (n: number): string => Number(n ?? 0).toFixed(6);

/** 一張清單嘅外殼（標題 ＋ 提示 ＋ 內容／空狀態） */
function Block({
  title,
  hint,
  empty,
  emptyText,
  children,
}: {
  title: string;
  hint: string;
  empty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
      <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{title}</h3>
      <p style={hintStyle}>{hint}</p>
      {empty ? <p style={hintStyle}>{emptyText}</p> : <div>{children}</div>}
    </section>
  );
}

/** 單行（單號 ＋ 目的地／收貨方 ＋ 交收時間 ＋ 去處理） */
function OrderRow({ order, note }: { order: PendingOrder; note?: string }) {
  const { t } = useTranslation();
  return (
    <div
      style={{
        padding: '10px 0',
        borderTop: '1px solid var(--color-divider)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <strong style={{ minWidth: '150px' }}>{order.orderNo}</strong>
      <span style={hintStyle}>
        {order.destinationLabel ?? order.destinationCode}
        {order.consigneeName ? ` · ${order.consigneeName}` : ''}
      </span>
      {order.deliveryCode && <span style={hintStyle}>{order.deliveryCode}</span>}
      {order.deliveredAt && (
        <span style={hintStyle}>
          {t('pending.deliveredAt')} {order.deliveredAt}
        </span>
      )}
      {note && <span style={{ ...hintStyle, color: 'var(--color-accent)' }}>{note}</span>}
      <Link to={`/outbound/${order.id}`} style={{ ...btnGhost, marginLeft: 'auto', textDecoration: 'none' }}>
        {t('pending.openOrder')}
      </Link>
    </div>
  );
}

export default function PendingLists({
  unsettledEvents,
  missingChop,
  onSite,
  onReload,
  reloading,
}: PendingListsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Block
        title={t('pending.unsettledTitle')}
        hint={t('pending.unsettledHint')}
        empty={unsettledEvents.length === 0}
        emptyText={t('pending.unsettledEmpty')}
      >
        {unsettledEvents.map((o) => (
          <OrderRow key={o.id} order={o} />
        ))}
      </Block>

      <Block
        title={t('pending.missingChopTitle')}
        hint={t('pending.missingChopHint')}
        empty={missingChop.length === 0}
        emptyText={t('pending.missingChopEmpty')}
      >
        {missingChop.map((o) => (
          <OrderRow key={o.id} order={o} note={t('pending.missingChopBadge')} />
        ))}
      </Block>

      <Block
        title={t('pending.onSiteTitle')}
        hint={t('pending.onSiteHint')}
        empty={onSite.length === 0}
        emptyText={t('pending.onSiteEmpty')}
      >
        {onSite.map((s) => (
          <div
            key={s.location}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--color-divider)',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <strong style={{ minWidth: '180px' }}>{s.orderNo ?? s.location}</strong>
            <span style={hintStyle}>
              {t('pending.cartons')} <span style={mono}>{s.cartons}</span>
            </span>
            <span style={hintStyle}>
              {t('pending.units')} <span style={mono}>{s.units}</span>
            </span>
            <span style={hintStyle}>
              {t('pending.cbm')} <span style={mono}>{fmt6(s.cbm)}</span>
            </span>
            <span style={hintStyle}>
              {t('pending.skus')} <span style={mono}>{s.skus}</span>
            </span>
          </div>
        ))}
      </Block>

      {onReload && (
        <p style={{ marginTop: '12px' }}>
          <button type="button" style={btnGhost} onClick={onReload} disabled={reloading}>
            {reloading ? t('common.loading') : t('pending.reload')}
          </button>
        </p>
      )}
    </>
  );
}
